import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { formatInr, variantText } from './retailUtils';
import { cartLabel, openCartsFor } from '../utils/curatedCarts';

/** The staff-only part of the product page (signed-in ADMIN / CURATOR / EMPLOYEE), carried over
 *  from the old product page: internal prices, approval of pending products (ADMIN), and adding
 *  the product to one of the staff member's collaborative curated carts.
 *  line: the current pick from usePurchase (variant + quantity). */
export default function StaffProductPanel({ product, role, line, quantity, onProductChange }) {
  const { addToast } = useToast();
  const [carts, setCarts] = useState(null);
  const [cartId, setCartId] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // The staff member's own open carts (see utils/curatedCarts) — not /quotes/my-carts, which
    // lists carts where they are the client and so was always empty for staff.
    openCartsFor({ role })
      .then(open => { if (!alive) return; setCarts(open); setCartId(open[0]?.id ?? ''); })
      .catch(() => alive && setCarts([]));
    return () => { alive = false; };
  }, [role]);

  const addToCart = async () => {
    if (!cartId) return;
    const variantLabel = line ? (line.label || variantText(line)) || null : null;
    setBusy(true);
    try {
      await api.post(`/quotes/${cartId}/add-item`, { productId: product.id, quantity, variantLabel });
      const name = carts.find(c => String(c.id) === String(cartId))?.cartName;
      addToast(`${quantity > 1 ? `${quantity} × ` : ''}${product.name} added to ${name || 'the curated cart'}`, 'success');
    } catch (err) {
      addToast(err.response?.data?.error || 'Could not add to the curated cart', 'error');
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    try {
      await api.put(`/products/${product.id}/approve`);
      onProductChange({ ...product, approvalStatus: 'APPROVED', adminFeedback: null });
      addToast('Product approved', 'success');
    } catch { addToast('Could not approve the product', 'error'); }
  };
  const reject = async () => {
    if (!feedback.trim()) return;
    try {
      await api.put(`/products/${product.id}/reject`, { feedback });
      onProductChange({ ...product, approvalStatus: 'REJECTED', adminFeedback: feedback });
      setRejecting(false); setFeedback('');
      addToast('Product rejected and sent back', 'success');
    } catch { addToast('Could not reject the product', 'error'); }
  };

  const pending = product.approvalStatus === 'PENDING';
  const rejected = product.approvalStatus === 'REJECTED';

  return (
    <div className="rt-staff sf-staff">
      <strong>eYantra staff only</strong>
      {(pending || rejected) && (
        <div className="sf-staff-status">
          {pending ? `Pending approval${product.createdBy ? ` — submitted by ${product.createdBy}` : ''}` : 'Rejected — needs changes'}
          {rejected && product.adminFeedback && <div>Reason: {product.adminFeedback}</div>}
          {pending && role === 'ADMIN' && !rejecting && (
            <div className="sf-staff-row">
              <button type="button" className="rt-btn rt-btn--sm" onClick={approve}>Approve</button>
              <button type="button" className="rt-btn rt-btn--ghost rt-btn--sm" onClick={() => setRejecting(true)}>Reject</button>
            </div>
          )}
          {rejecting && (
            <div className="sf-staff-row">
              <input className="rt-input" placeholder="What needs to change?" value={feedback} onChange={e => setFeedback(e.target.value)} aria-label="Rejection reason" />
              <button type="button" className="rt-btn rt-btn--sm" disabled={!feedback.trim()} onClick={reject}>Send back</button>
              <button type="button" className="rt-link" onClick={() => setRejecting(false)}>Cancel</button>
            </div>
          )}
        </div>
      )}
      <div>Purchase price: {product.eyantraPurchasePriceWithGst != null ? formatInr(product.eyantraPurchasePriceWithGst) : '—'}</div>
      <div>Seller: {product.sellerName || '—'}</div>
      <div>In stock: {product.itemsAvailable ?? '—'} units</div>
      {line?.sku && <div>SKU: {line.sku}</div>}
      {carts && carts.length > 0 && (
        <div className="sf-staff-row">
          <label className="rt-sr-only" htmlFor="sf-cart">Curated cart</label>
          <select id="sf-cart" className="rt-input" value={cartId} onChange={e => setCartId(e.target.value)}>
            {carts.map(c => <option key={c.id} value={c.id}>{cartLabel(c)}</option>)}
          </select>
          <button type="button" className="rt-btn rt-btn--sm" disabled={busy || !cartId} onClick={addToCart}>
            Add {quantity > 1 ? `${quantity} ` : ''}to curated cart
          </button>
        </div>
      )}
      {carts && carts.length === 0 && <div className="sf-staff-muted">No open curated carts — create one from Curated carts.</div>}
    </div>
  );
}
